export default {
  name: 'AlertSmartEventModal',
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    eventData: {
      type: Object,
      default: null
    },
    bucketEvents: {
      type: Array,
      default: () => []
    },
    loadingEvents: {
      type: Boolean,
      default: false
    },
    // Navegación
    currentIndex: {
      type: Number,
      default: 0
    },
    totalEvents: {
      type: Number,
      default: 0
    },
    canNavigatePrevious: {
      type: Boolean,
      default: false
    },
    canNavigateNext: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      imageError: false,
      videoLoading: false,
      videoError: false,
      videoData: null,
      // Paginado de la tabla
      currentPage: 1,
      itemsPerPage: 10,
      // Counter: rango seleccionado para Detail Event Information (start/end en ms)
      selectedEventRange: null,
      // Counter: eventos del rango (smart_event, Alert, LPR) cargados por el modal
      detailEventsList: [],
      loadingDetailEvents: false,
      // Bbox hover
      currentHoverEvent: null,
      currentHoverEventVideoTime: null, // Tiempo en segundos del video donde debe aparecer el evento
      lastVideoSearchTimestamp: null, // evita re-disparar búsqueda en hover
      pendingHoverEvent: null, // evento a posicionar luego de cargar nuevo clip
      bboxCanvasContext: null
    }
  },
  computed: {
    eventType() {
      if (!this.eventData || !this.eventData.metadata) return 'unknown';
      
      if (this.eventData.metadata.isAlert) return 'alert';
      if (this.eventData.metadata.isSmartEvent) return 'smart_event';
      if (this.eventData.metadata.isLPR) return 'lpr';
      if (this.eventData.metadata.isAction) return 'action';
      if (this.eventData.metadata.isObject) return 'object';
      if (this.eventData.metadata.isAxle) return 'axle';
      if (this.eventData.metadata.isCounter) return 'counter';
      
      return 'unknown';
    },
    totalPages() {
      const total = this.getEventsList().length;
      return Math.ceil(total / this.itemsPerPage);
    }
  },
  watch: {
    eventData: {
      handler(newData) {
        if (newData && this.isVisible) {
          this.loadVideoForEvent();
        }
      },
      deep: true,
      immediate: true
    },
    bucketEvents: {
      handler(newEvents, oldEvents) {
        this.currentPage = 1;
        this.selectedEventRange = null;
      },
      deep: true,
      immediate: true
    },
    isVisible(newValue) {
      if (newValue && this.eventData) {
        this.loadVideoForEvent();
      } else {
        this.resetVideoData();
      }
    }
  },
  mounted() {
    if (this.eventData && this.isVisible) {
      this.loadVideoForEvent();
    }
  },
  methods: {
    closeModal() {
      this.$emit('close');
      this.resetVideoData();
    },
    
    getEventTitle() {
      const typeMap = {
        'alert': 'alert_details',
        'smart_event': 'smart_event_details',
        'lpr': 'lpr_event_details',
        'action': 'action_details',
        'object': 'object_details',
        'axle': 'axle_details',
        'counter': 'counter_details',
        'unknown': 'event_details'
      };
      const key = typeMap[this.eventType] || 'event_details';
      return this.$ml.get(key) || key;
    },
    
    getEventSubtitle() {
      if (!this.eventData) return '';
      
      // Formato: SMART-CARROT CARROT (258)
      let category = '';
      let title = '';
      let count = this.getEventCount();
      
      if (this.eventData.metadata) {
        if (this.eventData.metadata.category) {
          category = this.eventData.metadata.category.toUpperCase();
        }
        if (this.eventData.metadata.objectType) {
          title = this.eventData.metadata.objectType.toUpperCase();
        } else if (this.eventData.metadata.alertType) {
          title = this.eventData.metadata.alertType.toUpperCase();
        } else if (this.eventData.metadata.eventType) {
          title = this.eventData.metadata.eventType.toUpperCase();
        }
      }
      
      // Si no hay category, usar content
      if (!category && this.eventData.content) {
        const parts = this.eventData.content.split(' ');
        if (parts.length > 0) {
          category = parts[0].replace(/[📹🧠🚨🚗⚡🎯]/g, '').trim().toUpperCase();
        }
      }
      
      // Si no hay title, usar category
      if (!title && category) {
        title = category;
      }
      
      const eventLabel = this.$ml.get('event') || 'EVENT';
      const subtitle = category && title ? `${category} ${title}` : (category || title || eventLabel);
      return `${subtitle} (${count})`;
    },
    
    getEventCount() {
      // Si hay eventos cargados, usar su longitud
      if (this.bucketEvents && Array.isArray(this.bucketEvents) && this.bucketEvents.length > 0) {
        return this.bucketEvents.length;
      }
      // Si no, intentar desde metadata
      if (this.eventData && this.eventData.metadata) {
        return this.eventData.metadata.eventCount || 
               this.eventData.metadata.cantidadEventos || 
               0;
      }
      return 0;
    },
    
    getEventsList() {
      // Retornar los eventos del bucket recibidos como prop
      const events = this.bucketEvents || [];
     
      return events;
    },
    
    // Métodos para paginado de la tabla
    getPaginatedEvents() {
      const events = this.getEventsList();
      if (!events || events.length === 0) return [];
      
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return events.slice(start, end);
    },
    
    getEventNumber(index) {
      return (this.currentPage - 1) * this.itemsPerPage + index + 1;
    },

    getEventTimestampMs(event) {
      if (!event) return NaN;
      const v = event.unix_itme_stamp || event.unix_timestamp || event.timestamp || event.date || event.createdAt || event.startTimestamp || event.start || event.time || event.event_time;
      if (v == null || v === '') return NaN;
      if (typeof v === 'object' && v.getTime) return v.getTime();
      const num = Number(v);
      if (Number.isFinite(num)) return num;
      if (typeof v === 'string' && /^\d+$/.test(v)) return parseInt(v, 10);
      const parsed = new Date(v).getTime();
      return Number.isFinite(parsed) ? parsed : NaN;
    },

    getEventEndTs(event, indexInPage) {
      const list = this.getEventsList();
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const idx = start + indexInPage;
      const eventStartMs = this.getEventTimestampMs(event);
      if (idx >= 0 && idx < list.length - 1) {
        const nextTs = this.getEventTimestampMs(list[idx + 1]);
        if (Number.isFinite(nextTs)) return nextTs;
      }
      const endTs = this.getEndTimestamp();
      const fallback = Number.isFinite(endTs) ? endTs : eventStartMs;
      // Nunca devolver un end anterior al start del evento (p. ej. última fila con bucket end < event start)
      return Number.isFinite(eventStartMs) && fallback < eventStartMs ? eventStartMs : fallback;
    },

    formatEventEndForRow(event, indexInPage) {
      const endTs = this.getEventEndTs(event, indexInPage);
      if (!Number.isFinite(endTs)) return '-';
      try {
        return new Date(endTs).toLocaleString('es-ES', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        });
      } catch (e) {
        return '-';
      }
    },

    onEventRowClick(event, indexInPage) {
      const startMs = this.getEventTimestampMs(event);
      const endMs = this.getEventEndTs(event, indexInPage);
      console.log('[AlertSmartEventModal] Event Information row clicked', {
        event,
        indexInPage,
        startMs: Number.isFinite(startMs) ? startMs : null,
        startDate: Number.isFinite(startMs) ? new Date(startMs).toISOString() : null,
        endMs: Number.isFinite(endMs) ? endMs : null,
        endDate: Number.isFinite(endMs) ? new Date(endMs).toISOString() : null,
        bucketEventsCount: (this.bucketEvents || []).length,
        bucketEventTypes: (this.bucketEvents || []).map((e) => ({
          ts: this.getEventTimestampMs(e),
          type: e.type || e.eventType || e.metadata?.type || '(no type)'
        }))
      });
      if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) {
        console.warn('[AlertSmartEventModal] Row click ignored: invalid start/end', { startMs, endMs });
        return;
      }
      this.selectedEventRange = { start: startMs, end: endMs };
      this.loadDetailEventsForRange({ start: startMs, end: endMs });
      console.log('[AlertSmartEventModal] selectedEventRange set, loading detail events', this.selectedEventRange);
    },

    isEventRowSelected(event) {
      if (!this.selectedEventRange) return false;
      const startMs = this.getEventTimestampMs(event);
      return startMs === this.selectedEventRange.start;
    },

    getDetailEventsList() {
      if (!this.selectedEventRange) return [];
      return this.detailEventsList || [];
    },

    async loadDetailEventsForRange({ start, end }) {
      if (!start || !end) return;
      if (end < start) end = start;
      if (start === end) end = start + 1000;
      this.loadingDetailEvents = true;
      this.detailEventsList = [];
      const apiUrl = (this.$URL || '') + '/api/v1/records/events_by_range';
      const types = ['smartEvent', 'Alert', 'LPR', 'AVC'];
      try {
        const results = await Promise.all(
          types.map((type) =>
            this.$axios.post(apiUrl, {
              startTimestamp: start,
              endTimestamp: end,
              type: type
            })
          )
        );
        const all = [];
        results.forEach((response, index) => {
          if (!response || !response.data) return;
          const sourceType = types[index] || 'unknown';
          let events = [];
          if (Array.isArray(response.data)) events = response.data;
          else if (response.data.events && Array.isArray(response.data.events)) events = response.data.events;
          else if (response.data.data && Array.isArray(response.data.data)) events = response.data.data;
          else if (response.data.rows && Array.isArray(response.data.rows)) events = response.data.rows;
          events.forEach((e) => {
            all.push({ ...e, _sourceType: sourceType });
          });
        });
        const ts = (e) => {
          const v = e.unix_itme_stamp || e.timestamp || e.date || e.createdAt || e.startTimestamp || e.start;
          if (v == null) return 0;
          return typeof v === 'object' && v.getTime ? v.getTime() : Number(v) || 0;
        };
        all.sort((a, b) => ts(a) - ts(b));
        this.detailEventsList = all;
      } catch (err) {
        console.error('Error loading detail events by range:', err);
        this.detailEventsList = [];
      } finally {
        this.loadingDetailEvents = false;
      }
    },

    getEvidenceUrl(event) {
      if (!event) return null;
      const url = event.vehicle_image_url || event.url_evidence || event.evidence || event.img || event.image;
      if (!url || typeof url !== 'string' || url === '/img/no_img_found.png') return null;
      const base = this.$URL || '';
      if (base && url.startsWith('/')) return base.replace(/\/$/, '') + url;
      return url;
    },

    getDetailEventCategory(event) {
      if (!event) return '-';
      // Para AVC: category = vehicle_type
      if (event.vehicle_type) return event.vehicle_type;
      // Para otros tipos: motive_categorie
      return event.motive_categorie || '-';
    },

    getDetailEventTag(event) {
      if (!event) return '-';
      // Para AVC: tag = axle_count
      if (event.axle_count != null) return event.axle_count.toString();
      // Para otros tipos: tag
      return event.tag || '-';
    },

    getDetailEventSource(event) {
      if (!event || !event._sourceType) return '-';
      const key = 'event_source_' + event._sourceType.toLowerCase();
      const label = this.$ml.get(key);
      return label && label !== key ? label : event._sourceType;
    },
    
    getPaginationStart() {
      if (this.getEventsList().length === 0) return 0;
      return (this.currentPage - 1) * this.itemsPerPage + 1;
    },

    getShowingEventsRangeText() {
      const s = this.$ml.get('showing_events_range') || 'Showing {0} - {1} of {2} events';
      return s.replace('{0}', this.getPaginationStart()).replace('{1}', this.getPaginationEnd()).replace('{2}', this.getEventsList().length);
    },

    getPageOfText() {
      const s = this.$ml.get('page_of') || 'Page {0} of {1}';
      return s.replace('{0}', this.currentPage).replace('{1}', this.totalPages);
    },
    
    getPaginationEnd() {
      const total = this.getEventsList().length;
      const end = this.currentPage * this.itemsPerPage;
      return end > total ? total : end;
    },
    
    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },
    
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },
    
    // Métodos helper para acceder a los timestamps del bucket
    getStartTimestamp() {
      if (!this.eventData) return null;
      
      // Intentar desde metadata primero
      if (this.eventData.metadata && this.eventData.metadata.startTimestamp) {
        return this.eventData.metadata.startTimestamp;
      }
      
      // Intentar desde el objeto start
      if (this.eventData.start) {
        return typeof this.eventData.start === 'object' 
          ? this.eventData.start.getTime() 
          : this.eventData.start;
      }
      
      return null;
    },
    
    getEndTimestamp() {
      if (!this.eventData) return null;
      
      // Intentar desde metadata primero
      if (this.eventData.metadata && this.eventData.metadata.endTimestamp) {
        return this.eventData.metadata.endTimestamp;
      }
      
      // Intentar desde el objeto end
      if (this.eventData.end) {
        return typeof this.eventData.end === 'object' 
          ? this.eventData.end.getTime() 
          : this.eventData.end;
      }
      
      return null;
    },
    
    // Método para obtener los datos del bucket necesarios para buscar eventos
    getBucketSearchParams() {
      return {
        startTimestamp: this.getStartTimestamp(),
        endTimestamp: this.getEndTimestamp(),
        eventId: this.eventData?.id || null,
        eventType: this.eventType,
        isBucket: this.eventData?.metadata?.isBucket || false,
        eventCount: this.getEventCount()
      };
    },
    
    formatDateTime(dateString) {
      if (!dateString) return 'N/A';
      try {
        const date = new Date(dateString);
        return date.toLocaleString('es-ES', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        });
      } catch (error) {
        return 'N/A';
      }
    },
    
    formatDuration(ms) {
      if (!ms || ms < 0) return '0s';
      const seconds = Math.floor(ms / 1000);
      const minutes = Math.floor(seconds / 60);
      const hours = Math.floor(minutes / 60);
      const days = Math.floor(hours / 24);
      
      if (days > 0) return `${days}d ${hours % 24}h ${minutes % 60}m`;
      if (hours > 0) return `${hours}h ${minutes % 60}m`;
      if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
      return `${seconds}s`;
    },
    
    formatEventDate(event) {
      if (!event) return '-';
      
      // Intentar diferentes campos de fecha, priorizando unix_itme_stamp para eventos del bucket
      let dateValue = event.unix_itme_stamp || 
                     event.timestamp || 
                     event.date || 
                     event.createdAt || 
                     event.startTimestamp ||
                     event.start;
      
      if (!dateValue) return '-';
      
      try {
        // Si es un string numérico (timestamp Unix), convertirlo a número
        if (typeof dateValue === 'string' && /^\d+$/.test(dateValue)) {
          dateValue = parseInt(dateValue, 10);
        }
        
        const date = new Date(dateValue);
        if (isNaN(date.getTime())) return '-';
        
        return date.toLocaleString('es-ES', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        });
      } catch (error) {
        console.warn('Error formatting event date:', error, dateValue);
        return dateValue.toString();
      }
    },
    
    async loadVideoForEvent() {
      if (!this.eventData || !this.eventData.metadata) {
        console.log('No event data available for video search');
        return;
      }
      
      
      
      this.videoLoading = true;
      this.videoError = false;
      this.videoData = null;
      
      try {
        const timestamp = this.eventData.metadata.unixTimestamp || this.eventData.start;
        const deviceId = this.eventData.metadata.deviceId;
        
        
        
        if (timestamp && deviceId) {
          this.$emit('search-video', {
            timestamp: timestamp,
            deviceId: deviceId,
            eventData: this.eventData
          });
        } else {
          console.warn('Missing timestamp or deviceId for video search');
          this.videoError = true;
          this.videoLoading = false;
        }
      } catch (error) {
        console.error('Error loading video:', error);
        this.videoError = true;
        this.videoLoading = false;
      }
    },
    
    onVideoLoaded() {
      this.videoLoading = false;
      this.videoError = false;
      this.initializeBboxCanvas();

      // Si se pidió buscar otro clip por hover, posicionar y dibujar bbox al cargar
      this.$nextTick(() => {
        if (this.pendingHoverEvent) {
          const ev = this.pendingHoverEvent;
          this.pendingHoverEvent = null;
          this.seekAndDrawForHoverEvent(ev);
        }
      });
    },
    
    onVideoTimeUpdate() {
      // Redibujar bbox si hay un evento en hover y el tiempo del video corresponde
      if (this.currentHoverEvent && this.currentHoverEventVideoTime !== null) {
        const video = this.$refs.eventVideo;
        if (video) {
          const currentVideoTime = video.currentTime || 0;
          const expectedTime = this.currentHoverEventVideoTime;
          const timeDifference = Math.abs(currentVideoTime - expectedTime);
          const tolerance = 0.5; // Tolerancia de 0.5 segundos
          
          // Solo redibujar si el tiempo está dentro de la tolerancia
          if (timeDifference <= tolerance) {
            this.drawBbox();
          } else {
            // Si el tiempo no corresponde, limpiar el bbox
            this.clearBbox();
          }
        }
      }
    },
    
    initializeBboxCanvas() {
      this.$nextTick(() => {
        const canvas = this.$refs.bboxCanvas;
        const video = this.$refs.eventVideo;
        
        if (canvas && video) {
          // Sincronizar tamaño del canvas con el video
          const updateCanvasSize = () => {
            if (!canvas || !video) return;
            
            // Obtener dimensiones del contenedor
            const containerWidth = video.offsetWidth;
            const containerHeight = video.offsetHeight;
            
            // Establecer tamaño del canvas igual al contenedor
            canvas.width = containerWidth;
            canvas.height = containerHeight;
            canvas.style.width = containerWidth + 'px';
            canvas.style.height = containerHeight + 'px';
            
            // Asegurar que el contexto tenga fondo transparente
            if (this.bboxCanvasContext) {
              this.bboxCanvasContext.clearRect(0, 0, canvas.width, canvas.height);
            }
          };
          
          // Inicializar contexto
          this.bboxCanvasContext = canvas.getContext('2d');
          
          // Actualizar tamaño inicial
          updateCanvasSize();
          
          // Actualizar cuando el video se carga
          video.addEventListener('loadedmetadata', updateCanvasSize);
          video.addEventListener('resize', updateCanvasSize);
          
          // Actualizar también cuando cambia el tamaño de la ventana
          window.addEventListener('resize', updateCanvasSize);
        }
      });
    },
    
    onRowHover(event) {
      this.currentHoverEvent = event;
      
      if (!this.$refs.eventVideo || !event) return;
      
      const video = this.$refs.eventVideo;

      // Validar si el evento cae dentro del clip de video que está cargado actualmente
      // (no el rango del bucket). Se usa el timestamp de inicio del clip + duración.
      const eventTimestampMs = event?.unix_itme_stamp ? Number(event.unix_itme_stamp) : NaN;
      const clipStartMs = this.videoData?.searchResult?.timestamp ? Number(this.videoData.searchResult.timestamp) : NaN;
      const durationMs = (video && Number.isFinite(video.duration)) ? video.duration * 1000 : NaN;
      const isEventInCurrentVideo =
        Number.isFinite(eventTimestampMs) &&
        Number.isFinite(clipStartMs) &&
        Number.isFinite(durationMs) &&
        eventTimestampMs >= clipStartMs &&
        eventTimestampMs <= (clipStartMs + durationMs);
      console.log('[AlertSmartEventModal] event_in_current_video', isEventInCurrentVideo);

      // Si no está en el clip actual, buscar/cargar el video correspondiente al timestamp del evento
      if (!isEventInCurrentVideo && Number.isFinite(eventTimestampMs)) {
        const deviceId = this.eventData?.metadata?.deviceId || event?.deviceId || null;
        if (deviceId) {
          // Guard para no spamear la misma búsqueda mientras carga
          if (this.videoLoading && this.lastVideoSearchTimestamp === eventTimestampMs) {
            return;
          }
          this.pendingHoverEvent = event;
          this.lastVideoSearchTimestamp = eventTimestampMs;
          this.videoLoading = true;
          this.videoError = false;
          this.videoData = null;
          this.$emit('search-video', {
            timestamp: eventTimestampMs,
            deviceId,
            eventData: this.eventData,
            hoverEvent: event
          });
        }
        return;
      }
      
      // Obtener timestamp del evento
      let timestamp = null;
      if (event.unix_itme_stamp) {
        // Convertir timestamp Unix a segundos desde el inicio del video
        const eventTimestamp = parseInt(event.unix_itme_stamp);
        const videoStartTimestamp = this.videoData?.searchResult?.timestamp || this.getStartTimestamp();
        
        if (videoStartTimestamp && eventTimestamp) {
          // Calcular la diferencia en segundos
          const diffMs = eventTimestamp - videoStartTimestamp;
          timestamp = Math.max(0, diffMs / 1000);
        }
      }
      
      // Si no se puede calcular desde el inicio, intentar desde el timestamp absoluto del video
      if (timestamp === null && event.unix_itme_stamp && this.videoData?.searchResult?.timestamp) {
        const eventTimestamp = parseInt(event.unix_itme_stamp);
        const videoDataTimestamp = this.videoData.searchResult.timestamp;
        
        if (videoDataTimestamp) {
          const diffMs = eventTimestamp - videoDataTimestamp;
          timestamp = Math.max(0, diffMs / 1000);
        }
      }
      
      // Guardar el tiempo del video donde debe aparecer este evento
      this.currentHoverEventVideoTime = timestamp;
      
      // Posicionar el video en el timestamp
      if (timestamp !== null && !isNaN(timestamp) && video.duration) {
        const targetTime = Math.min(timestamp, video.duration);
        video.currentTime = targetTime;
      }
      
      // Dibujar bbox después de un pequeño delay para que el video se posicione
      this.$nextTick(() => {
        setTimeout(() => {
          this.drawBbox();
        }, 200);
      });
    },

    seekAndDrawForHoverEvent(event) {
      const video = this.$refs.eventVideo;
      if (!video || !event) return;

      // Calcular segundo exacto del evento dentro del clip actual
      const eventMs = event?.unix_itme_stamp ? Number(event.unix_itme_stamp) : NaN;
      const clipStartMs = this.videoData?.searchResult?.timestamp ? Number(this.videoData.searchResult.timestamp) : NaN;
      const bucketStartMs = this.getStartTimestamp() ? Number(this.getStartTimestamp()) : NaN;

      let seconds = null;
      if (Number.isFinite(eventMs) && Number.isFinite(clipStartMs)) {
        seconds = Math.max(0, (eventMs - clipStartMs) / 1000);
      } else if (Number.isFinite(eventMs) && Number.isFinite(bucketStartMs)) {
        seconds = Math.max(0, (eventMs - bucketStartMs) / 1000);
      }

      this.currentHoverEvent = event;
      this.currentHoverEventVideoTime = seconds;

      // Posicionar y luego dibujar bbox
      if (seconds !== null && !Number.isNaN(seconds) && Number.isFinite(video.duration)) {
        const targetTime = Math.min(seconds, video.duration);

        const onSeeked = () => {
          video.removeEventListener('seeked', onSeeked);
          this.$nextTick(() => {
            this.drawBbox();
          });
        };
        video.addEventListener('seeked', onSeeked);
        video.currentTime = targetTime;

        // Fallback por si el evento seeked no dispara en algunos navegadores
        setTimeout(() => {
          this.drawBbox();
        }, 250);
      } else {
        this.$nextTick(() => {
          this.drawBbox();
        });
      }
    },
    
    onRowLeave() {
      this.currentHoverEvent = null;
      this.currentHoverEventVideoTime = null;
      this.clearBbox();
    },
    
    drawBbox() {
      if (!this.bboxCanvasContext || !this.currentHoverEvent || !this.$refs.eventVideo) {
        return;
      }
      
      const canvas = this.$refs.bboxCanvas;
      const video = this.$refs.eventVideo;
      const ctx = this.bboxCanvasContext;
      const event = this.currentHoverEvent;
      
      // Asegurar que el canvas tenga el tamaño correcto
      if (canvas.width !== video.offsetWidth || canvas.height !== video.offsetHeight) {
        canvas.width = video.offsetWidth;
        canvas.height = video.offsetHeight;
      }
      
      // Limpiar canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Obtener bbox del evento
      let bbox = null;
      if (event.bbox) {
        try {
          // Si bbox es un string JSON, parsearlo
          if (typeof event.bbox === 'string') {
            bbox = JSON.parse(event.bbox);
          } else {
            bbox = event.bbox;
          }
        } catch (e) {
          console.warn('Error parsing bbox:', e);
          return;
        }
      }
      
      if (!bbox || (bbox.x === undefined) || (bbox.y === undefined) || (bbox.w === undefined) || (bbox.h === undefined)) {
        return;
      }
      
      // Obtener dimensiones reales del video
      const videoWidth = video.videoWidth || video.offsetWidth;
      const videoHeight = video.videoHeight || video.offsetHeight;
      const displayWidth = video.offsetWidth;
      const displayHeight = video.offsetHeight;
      
      // Calcular escala si el video está escalado
      const scaleX = displayWidth / videoWidth;
      const scaleY = displayHeight / videoHeight;
      
      // Calcular posición y tamaño del bbox en píxeles de display
      const fx = parseFloat(bbox.x) * displayWidth;
      const fy = parseFloat(bbox.y) * displayHeight;
      const w = parseFloat(bbox.w) * displayWidth;
      const h = parseFloat(bbox.h) * displayHeight;
      
      // Dibujar rectángulo
      ctx.strokeStyle = '#ff0000';
      ctx.lineWidth = 3;
      ctx.strokeRect(fx, fy, w, h);
      
      // Opcional: agregar etiqueta con fondo
      if (event.tag) {
        ctx.fillStyle = 'rgba(255, 0, 0, 0.8)';
        ctx.fillRect(fx, fy - 20, w, 20);
        ctx.fillStyle = '#ffffff';
        ctx.font = '12px Arial';
        ctx.fillText(event.tag, fx + 5, fy - 5);
      }
    },
    
    clearBbox() {
      if (this.bboxCanvasContext && this.$refs.bboxCanvas) {
        const canvas = this.$refs.bboxCanvas;
        // Limpiar completamente el canvas para mantener transparencia
        this.bboxCanvasContext.clearRect(0, 0, canvas.width, canvas.height);
      }
    },

    onVideoError() {
      this.videoError = true;
      this.videoLoading = false;
    },

    resetVideoData() {
      this.videoData = null;
      this.videoLoading = false;
      this.videoError = false;
      this.imageError = false;
      this.currentHoverEvent = null;
      this.clearBbox();
    },

    setVideoData(videoData) {
      this.videoData = videoData;
      this.videoLoading = false;
      this.videoError = false;

      if (!videoData || !videoData.videoUrl) {
        this.videoData = null;
        this.videoError = false;
        this.videoLoading = false;
      } else {
        this.$nextTick(() => {
          this.initializeBboxCanvas();
        });
      }
    },

    navigateToPrevious() {
      this.$emit('navigate-previous');
    },

    navigateToNext() {
      this.$emit('navigate-next');
    }
  }
}
